/**
 * Auth Telemetry Tracker
 * 
 * A component that tracks authentication events when users sign in/out.
 * Also handles pending invitation application after signup.
 * Also syncs Clerk user info to the userStore.
 * 
 * Makes C1 (Observability: Login success/failure captured) true.
 * Evidence: audit_events table contains LoginSucceeded rows
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { recordLoginSuccess, recordLogout } from '../lib/authTelemetry';
import { 
  getPendingInvite, 
  clearPendingInvite, 
  applyInvitation,
  validateInvitation,
} from '../lib/invitationService';
import { getRoleAssignments } from '../lib/rolesService';
import { getSupabaseClient } from '../lib/supabaseClient';
import { isSuperAdminBySupabaseUserId } from '../lib/superAdminService';
import { useUserStore } from '../stores/prioritization/userStore';
import { useTenantStore } from '../stores/prioritization/tenantStore';
import type { ActorRole } from '../types/prioritization/roles';

/** True while the store still names this user — false once it has been signed out or switched. */
function isStillCurrentUser(userId: string): boolean {
  return useUserStore.getState().userId === userId;
}

/**
 * Tracks auth events for observability.
 * Also applies pending invitations after signup/signin.
 * Also syncs user info and roles to the userStore.
 * Should be placed inside <SignedIn> to track when users are authenticated.
 */
export function AuthTelemetryTracker() {
  const navigate = useNavigate();
  const hasRecordedLogin = useRef(false);
  const hasCheckedInvite = useRef(false);
  const previousUserId = useRef<string | null>(null);

  const userId = useUserStore(state => state.userId);
  const userName = useUserStore(state => state.userName);
  const userEmail = useUserStore(state => state.userEmail);
  const setUser = useUserStore(state => state.setUser);
  const switchRole = useUserStore(state => state.switchRole);

  const isSignedIn = !!(userId && userId !== 'unknown');

  useEffect(() => {
    if (!isSignedIn || !userId) return;

    const trackAuth = async () => {
      if (!hasRecordedLogin.current) {
        hasRecordedLogin.current = true;

        try {
          // Telemetry must never gate workspace-context resolution: when
          // recordLoginSuccess threw (e.g. the record_user_login grant
          // regression), fetchTenants was skipped and the session kept a
          // stale persisted workspace — empty lists + RLS-refused writes.
          try {
            await recordLoginSuccess(userId, userEmail, 'default-tenant');
            console.log('[AuthTelemetry] Login success recorded for:', userEmail);
          } catch (telemetryErr) {
            console.warn('[AuthTelemetry] Login telemetry failed (continuing):', telemetryErr);
          }

          try {
            await useTenantStore.getState().fetchTenants(userId);
          } catch (tenantErr) {
            console.warn('[AuthTelemetry] Failed to fetch tenants/workspace:', tenantErr);
          }

          let invitationApplied = false;
          const isOnInvitePage = window.location.pathname.startsWith('/invite/');

          if (!hasCheckedInvite.current && !isOnInvitePage) {
            hasCheckedInvite.current = true;
            const pendingToken = getPendingInvite();

            if (pendingToken) {
              console.log('[AuthTelemetry] Found pending invitation, applying...');
              const validation = await validateInvitation(pendingToken, userEmail);

              if (validation.valid && validation.invitation) {
                const result = await applyInvitation(pendingToken, userId);
                if (result.success) {
                  clearPendingInvite();
                  invitationApplied = true;
                  // The workspaces above were read before this one was joined.
                  await useTenantStore.getState().fetchTenants(userId);
                  if (validation.invitation.workspaceId) {
                    useTenantStore.getState().setCurrentWorkspace(validation.invitation.workspaceId);
                  }
                } else {
                  clearPendingInvite();
                }
              } else {
                clearPendingInvite();
              }
            }
          }

          try {
            const isPlatformAdmin = await isSuperAdminBySupabaseUserId(userId);
            // Everything above awaited the network. If AuthSessionSync signed the
            // store out meanwhile (the session had expired or been revoked),
            // writing this user back would restore a signed-in shell whose every
            // read is refused — the page shows empty lists instead of sign-in.
            if (!isStillCurrentUser(userId)) return;
            setUser(userId, userName || 'User', userEmail || '');

            if (isPlatformAdmin) {
              switchRole('platform-admin');
            } else {
              const supabase = getSupabaseClient();
              const { data: allRoles } = await supabase.from('roles').select('id, name');
              const roleIdToName = new Map(
                (allRoles || []).map((r: { id: string; name: string }) => [r.id, r.name as ActorRole])
              );
              const assignments = await getRoleAssignments({ subjectType: 'user', subjectId: userId, activeOnly: true });
              const assignedRoleNames = assignments
                .map(a => roleIdToName.get(a.roleId))
                .filter((name): name is ActorRole => !!name);

              console.log('[AuthTelemetry] User roles from DB:', assignedRoleNames);
              if (!isStillCurrentUser(userId)) return;

              if (assignedRoleNames.length > 0 || invitationApplied) {
                let primaryRole: ActorRole | null = null;
                if (assignedRoleNames.includes('system-admin')) primaryRole = 'system-admin';
                else if (assignedRoleNames.includes('platform-admin')) primaryRole = 'platform-admin';
                else if (assignedRoleNames.includes('workspace-admin')) primaryRole = 'workspace-admin';
                else if (assignedRoleNames.length > 0) primaryRole = assignedRoleNames[0];
                if (primaryRole) switchRole(primaryRole);
              }
            }
          } catch (roleError) {
            console.error('[AuthTelemetry] Failed to fetch user roles:', roleError);
          }

          if (invitationApplied) {
            navigate('/prioritization', { replace: true });
          }
        } catch (error) {
          console.error('[AuthTelemetry] Failed to record login:', error);
        }
      }

      if (previousUserId.current && !isSignedIn) {
        try {
          await recordLogout('session-ended');
        } catch (error) {
          console.error('[AuthTelemetry] Failed to record logout:', error);
        }
        useTenantStore.getState().setCurrentTenant(null);
        useTenantStore.getState().setCurrentWorkspace(null);
        try {
          const { clearTenantContext } = await import('../lib/tenantContext');
          clearTenantContext();
        } catch {
          // tenantContext module optional in some builds
        }
        hasRecordedLogin.current = false;
        hasCheckedInvite.current = false;
      }

      previousUserId.current = userId ?? null;
    };

    trackAuth();
  }, [isSignedIn, userId, userName, userEmail, navigate, setUser, switchRole]);

  return null;
}

export default AuthTelemetryTracker;


